// src/pages/MatchSetup/Modals.js
//
// Two admin-facing modals for the match-setup page:
//
//   <AddMemberModal>
//     Three tabs:
//       1. Members      — regulars not in playing list (said 'no',
//                         rested, or absent); add to playing pool.
//       2. Guests       — existing GST### members not in this match;
//                         add to playing pool.
//       3. + New guest  — small form to create a brand-new GST### member
//                         and add them in one click.
//
//     All three flows END the same way: POST /playing-list/:memberId.
//     Captains pick the added person through the normal → A / → B
//     alternating draft.
//
//   <ConditionsModal>
//     Lets admin toggle a member's special conditions for this match:
//     no_bowling, no_keeping, leaving_early + leave_time.

import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';

// ─── AddMemberModal ─────────────────────────────────────────
export function AddMemberModal({ matchId, onClose, onAdded }) {
  const [tab, setTab] = useState('members');

  return (
    <div className="ms-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ms-modal">
        <div className="ms-modal-head">
          <h3>Add to playing list</h3>
          <button className="ms-icon-btn-sm" onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid var(--ms-gray-200)' }}>
          <TabBtn active={tab === 'members'} onClick={() => setTab('members')}>Members</TabBtn>
          <TabBtn active={tab === 'guests'}  onClick={() => setTab('guests')}>Guests</TabBtn>
          <TabBtn active={tab === 'new'}     onClick={() => setTab('new')}>+ New guest</TabBtn>
        </div>

        <div className="ms-modal-body">
          {tab === 'members' && <MembersTab matchId={matchId} onAdded={onAdded} />}
          {tab === 'guests'  && <GuestsTab  matchId={matchId} onAdded={onAdded} />}
          {tab === 'new'     && <NewGuestTab matchId={matchId} onAdded={onAdded} onCreated={() => setTab('guests')} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: '10px 8px', cursor: 'pointer',
        background: active ? 'var(--ms-navy)' : 'transparent',
        color: active ? 'white' : 'var(--ms-gray-600)',
        border: 'none', fontWeight: active ? 700 : 500, fontSize: 13,
        borderBottom: active ? '2px solid var(--ms-navy)' : '2px solid transparent',
      }}
    >
      {children}
    </button>
  );
}

// ─── Tab 1: Members ──────────────────────────────────────────
function MembersTab({ matchId, onAdded }) {
  const [list,   setList]   = useState(null);
  const [error,  setError]  = useState(null);
  const [busy,   setBusy]   = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/scorer/matches/${matchId}/addable-members`)
      .then(r => { if (alive) setList(r.data.members || []); })
      .catch(e => { if (alive) setError(e?.response?.data?.error || e.message || 'load failed'); });
    return () => { alive = false; };
  }, [matchId]);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(m =>
      (m.display_name || '').toLowerCase().includes(q) ||
      (m.ucc_id || '').toLowerCase().includes(q)
    );
  }, [list, search]);

  async function add(memberId) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/playing-list/${memberId}`);
      onAdded();
      setList(prev => prev ? prev.filter(m => m.id !== memberId) : prev);
    } catch (e) { alertError(e); }
    finally { setBusy(false); }
  }

  function statusLabel(m) {
    if (m.final_status === 'rested')          return 'rested';
    if (m.final_status === 'opt_out_full')    return 'opted out';
    if (m.final_status === 'opt_out_fee_only') return 'fee only';
    if (m.final_status === 'absent')          return 'absent';
    if (m.final_status === 'playing')         return null;
    if (m.game_response === 'yes')            return null;
    if (m.game_response === 'no')             return 'said no';
    return 'no reply';
  }

  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--ms-gray-500)', marginBottom: 8 }}>
        Members not in Saturday's playing list. Adding here puts them into the unassigned pool — captains pick them through the normal draft.
      </div>
      <input
        type="text"
        className="ms-team-input"
        placeholder="Search name or UCC ID…"
        autoFocus
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 10 }}
      />
      {error && <div style={{ color: 'var(--ms-red)', fontSize: 13 }}>{error}</div>}
      {!list && !error && <Spinner />}
      {list && filtered.length === 0 && (
        <div style={{ color: 'var(--ms-gray-500)', fontSize: 13, fontStyle: 'italic' }}>
          No matching members found.
        </div>
      )}
      {filtered.length > 0 && (
        <ScrollList>
          {filtered.map(m => (
            <Row
              key={m.id}
              name={m.display_name}
              tag={statusLabel(m)}
              onAdd={() => add(m.id)}
              busy={busy}
            />
          ))}
        </ScrollList>
      )}
    </>
  );
}

// ─── Tab 2: Guests ───────────────────────────────────────────
function GuestsTab({ matchId, onAdded }) {
  const [list,   setList]   = useState(null);
  const [error,  setError]  = useState(null);
  const [busy,   setBusy]   = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/scorer/matches/${matchId}/addable-guests`)
      .then(r => { if (alive) setList(r.data.guests || []); })
      .catch(e => { if (alive) setError(e?.response?.data?.error || e.message || 'load failed'); });
    return () => { alive = false; };
  }, [matchId]);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(m =>
      (m.display_name || '').toLowerCase().includes(q) ||
      (m.ucc_id || '').toLowerCase().includes(q)
    );
  }, [list, search]);

  async function add(memberId) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/playing-list/${memberId}`);
      onAdded();
      setList(prev => prev ? prev.filter(m => m.id !== memberId) : prev);
    } catch (e) { alertError(e); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--ms-gray-500)', marginBottom: 8 }}>
        Returning guests already in your records. Adding here puts them into the unassigned pool — captains pick them through the normal draft.
      </div>
      <input
        type="text"
        className="ms-team-input"
        placeholder="Search guest name or GST ID…"
        autoFocus
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 10 }}
      />
      {error && <div style={{ color: 'var(--ms-red)', fontSize: 13 }}>{error}</div>}
      {!list && !error && <Spinner />}
      {list && filtered.length === 0 && (
        <div style={{ color: 'var(--ms-gray-500)', fontSize: 13, fontStyle: 'italic' }}>
          {(list || []).length === 0
            ? 'No guests in records yet. Use the "+ New guest" tab to create one.'
            : 'No matching guests found.'}
        </div>
      )}
      {filtered.length > 0 && (
        <ScrollList>
          {filtered.map(m => (
            <Row
              key={m.id}
              name={m.display_name}
              tag={`${m.ucc_id} · ${m.past_appearances} past match${m.past_appearances === 1 ? '' : 'es'}`}
              guestBadge
              onAdd={() => add(m.id)}
              busy={busy}
            />
          ))}
        </ScrollList>
      )}
    </>
  );
}

// ─── Tab 3: + New guest ──────────────────────────────────────
function NewGuestTab({ matchId, onAdded, onCreated }) {
  const [name,    setName]    = useState('');
  const [jersey,  setJersey]  = useState('');
  const [mobile,  setMobile]  = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState(null);

  async function create() {
    if (busy) return;
    setError(null);
    if (!name.trim()) { setError('Please enter a display name.'); return; }
    setBusy(true);
    try {
      const guestRes = await api.post('/members/guest', {
        display_name: name.trim(),
        jersey_no:    jersey === '' ? null : Number(jersey),
        mobile:       mobile === '' ? null : mobile.trim(),
      });
      const newMember = guestRes.data.member;
      if (!newMember || !newMember.id) throw new Error('guest creation returned no member');
      await api.post(`/scorer/matches/${matchId}/playing-list/${newMember.id}`);
      onAdded();
      setName(''); setJersey(''); setMobile('');
      if (onCreated) onCreated();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'create failed');
    } finally { setBusy(false); }
  }

  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--ms-gray-500)', marginBottom: 12 }}>
        Brand new guest who hasn't played at UCC before. They'll be added to the unassigned pool. Captains pick them through the normal draft.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label="Display name *" htmlFor="ng-name">
          <input
            id="ng-name" type="text" className="ms-team-input"
            placeholder="e.g. Niranjan Sharma" autoFocus
            value={name} onChange={e => setName(e.target.value)}
            disabled={busy} maxLength={100}
          />
        </Field>
        <Field label="Jersey no. (optional)" htmlFor="ng-jersey">
          <input
            id="ng-jersey" type="number" min="0" max="999"
            className="ms-team-input"
            value={jersey} onChange={e => setJersey(e.target.value)}
            disabled={busy} style={{ width: 110 }}
          />
        </Field>
        <Field label="Mobile (optional)" htmlFor="ng-mobile">
          <input
            id="ng-mobile" type="tel" className="ms-team-input"
            value={mobile} onChange={e => setMobile(e.target.value)}
            disabled={busy} maxLength={20}
          />
        </Field>

        {error && <div style={{ color: 'var(--ms-red)', fontSize: 13 }}>{error}</div>}

        <button
          className="ms-btn-confirm"
          onClick={create}
          disabled={busy || !name.trim()}
          style={{ background: 'var(--ms-navy)', marginTop: 6 }}
        >
          {busy ? 'Creating…' : 'Create + add to playing list'}
        </button>
      </div>
    </>
  );
}

// ─── Shared inline components ────────────────────────────────
function Spinner() {
  return <div style={{ color: 'var(--ms-gray-500)', fontSize: 13 }}>Loading…</div>;
}
function ScrollList({ children }) {
  return (
    <div style={{ maxHeight: 360, overflowY: 'auto', borderTop: '1px solid var(--ms-gray-200)' }}>
      {children}
    </div>
  );
}
function Row({ name, tag, guestBadge, onAdd, busy }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 4px', borderBottom: '1px solid var(--ms-gray-100)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--ms-navy)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{name}</span>
          {guestBadge && (
            <span
              className="ms-tag"
              style={{ background: '#e0e7ff', color: '#3730a3', fontSize: 10, fontWeight: 700 }}
              title="Guest member"
            >
              👤 GST
            </span>
          )}
        </div>
        {tag && (
          <div style={{ fontSize: 10.5, color: 'var(--ms-gray-500)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {tag}
          </div>
        )}
      </div>
      <button
        className="ms-btn-confirm"
        disabled={busy}
        onClick={onAdd}
        style={{ background: 'var(--ms-navy)', minWidth: 70, fontSize: 13, padding: '6px 12px' }}
      >
        + Add
      </button>
    </div>
  );
}
function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 11, color: 'var(--ms-gray-500)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function alertError(e) {
  const data = e?.response?.data;
  const msg  = (data?.error || e.message || 'Action failed')
             + (data?.detail ? ' — ' + data.detail : '');
  alert(msg);
}


// ─── ConditionsModal (unchanged) ────────────────────────────
export function ConditionsModal({ matchId, member, canEdit, onClose, onSaved }) {
  const [noBowling,    setNoBowling]    = useState(!!member?.no_bowling);
  const [noKeeping,    setNoKeeping]    = useState(!!member?.no_keeping);
  const [leavingEarly, setLeavingEarly] = useState(!!member?.leaving_early);
  const [leaveTime,    setLeaveTime]    = useState(member?.leave_time ? String(member.leave_time).slice(0, 5) : '');
  const [busy,         setBusy]         = useState(false);

  async function save() {
    if (busy) return;
    if (leavingEarly && !leaveTime) {
      alert('Please pick a leave time, or untick "leaving early"');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/scorer/matches/${matchId}/availability/${member.id}`, {
        no_bowling: noBowling,
        no_keeping: noKeeping,
        leaving_early: leavingEarly,
        leave_time: leavingEarly ? leaveTime : null,
      });
      onSaved();
      onClose();
    } catch (e) { alertError(e); }
    finally { setBusy(false); }
  }

  return (
    <div className="ms-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ms-modal">
        <div className="ms-modal-head">
          <h3 style={{ color: 'var(--ms-navy)' }}>{member?.display_name}</h3>
          <button className="ms-icon-btn-sm" onClick={onClose}>×</button>
        </div>
        <div className="ms-modal-body">
          <div style={{ fontSize: 11, color: 'var(--ms-gray-500)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
            Special conditions
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: canEdit ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={noBowling}
              onChange={e => setNoBowling(e.target.checked)}
              disabled={!canEdit || busy}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ms-navy)' }}>🚫 Won't bowl</div>
              <div style={{ fontSize: 11, color: 'var(--ms-gray-500)' }}>Excluded from bowling rotation</div>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: canEdit ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={noKeeping}
              onChange={e => setNoKeeping(e.target.checked)}
              disabled={!canEdit || busy}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ms-navy)' }}>🚫 Won't keep</div>
              <div style={{ fontSize: 11, color: 'var(--ms-gray-500)' }}>Not available for wicket-keeping</div>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', cursor: canEdit ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={leavingEarly}
              onChange={e => {
                setLeavingEarly(e.target.checked);
                if (!e.target.checked) setLeaveTime('');
              }}
              disabled={!canEdit || busy}
              style={{ width: 18, height: 18, marginTop: 2 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ms-navy)' }}>⏱ Leaving early</div>
              <div style={{ fontSize: 11, color: 'var(--ms-gray-500)', marginBottom: 6 }}>Departure time today</div>
              {leavingEarly && (
                <input
                  type="time"
                  value={leaveTime}
                  onChange={e => setLeaveTime(e.target.value)}
                  disabled={!canEdit || busy}
                  className="ms-team-input"
                  style={{ width: 110 }}
                />
              )}
            </div>
          </label>
        </div>
        {canEdit && (
          <div className="ms-modal-foot">
            <button className="ms-btn-cancel-x" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="ms-btn-confirm" onClick={save} disabled={busy} style={{ background: 'var(--ms-navy)' }}>
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
